import { motion, useReducedMotion } from 'framer-motion'
import {
  Stethoscope,
  UserCog,
  ClipboardCheck,
  AlertTriangle,
  BarChart3,
  Target,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import ServiceInfoPopover from '@/components/ui/ServiceInfoPopover'
import { useMediaQuery } from '@/hooks/useMediaQuery'

/**
 * Servicios — sección reorganizada en 3 zonas verticales con jerarquía clara:
 *
 *   1. HEADER centrado: "Medicina + Análisis + Gestión" + tagline
 *   2. CONTENIDO grid 2 col: ¿Qué recibe tu empresa? (izq) + Familia FITLAB (der)
 *   3. DIRECCIONES en cards iguales abajo (médica + administrativa)
 *
 * Cambios vs versión anterior:
 *  - Heading ancla principal con tagline arriba (no debajo, donde flotaba sin contexto)
 *  - "¿Qué recibe tu empresa?" como card con fondo sutil + dividers, no items flotando
 *  - Sub-marcas con altura uniforme min-h-[140px], sin bullet tricolor lateral (ruido)
 *  - Direcciones en cards iguales con padding generoso + icon grande
 */

type SubBrandId = 'screening' | 'consultores' | 'cemsa' | 'fitlapp'

type SubBrand = {
  id: SubBrandId
  logo: string
  alt: string
  title: string
  description: string
}

const SUB_BRANDS: SubBrand[] = [
  {
    id: 'screening',
    logo: '/brand/logo-screening-fitlab.jpg',
    alt: 'Logo Screening FITLAB',
    title: 'SCREENING FITLAB',
    description:
      'Evaluación integral de salud laboral que identifica riesgos y transforma datos en decisiones concretas para la empresa.',
  },
  {
    id: 'consultores',
    logo: '/brand/logo-fitlab-consultores.jpg',
    alt: 'Logo Fitlab Consultores',
    title: 'FITLAB CONSULTORES',
    description:
      'Área especializada de Fitlab orientada a salud ocupacional y asesoría técnica para empresas. Brindamos soporte médico-profesional en aplicación de normativa laboral, creación de protocolos, análisis de enfermedades y estrategias preventivas, integrando criterios clínicos, técnicos y de gestión para apoyar decisiones empresariales.',
  },
  {
    id: 'cemsa',
    logo: '/brand/logo-cemsa-fitlab.jpg',
    alt: 'Logo Cemsa & Fitlab — Salud Deportiva Laboral',
    title: 'CEMSA FITLAB',
    description:
      'Alianza estratégica con centro médico CEMSA y Gimnasios CEMSA para brindar intervenciones médicas, nutricionales y deportivas de forma integrada.',
  },
  {
    id: 'fitlapp',
    logo: '/brand/logo-fitlapp.jpg',
    alt: 'Logo Fitlapp — iOS y Android',
    title: 'FITLAPP',
    description:
      'Plataforma digital complementaria a Screening Fitlab, orientada a la gestión y análisis de información en salud laboral. Facilita la organización de datos, el seguimiento de resultados y la generación de informes para apoyar decisiones dentro de la empresa. Disponible en iOS y Android.',
  },
]

function SubBrandCard({
  brand,
  index,
  isOpen,
  onClick,
}: {
  brand: SubBrand
  index: number
  isOpen: boolean
  onClick: () => void
}) {
  const reduced = useReducedMotion()
  return (
    <motion.button
      type="button"
      onClick={onClick}
      aria-expanded={isOpen}
      aria-haspopup="dialog"
      aria-controls={`brand-info-title-${brand.id}`}
      aria-label={`Ver descripción de ${brand.title}`}
      initial={reduced ? undefined : { opacity: 0, y: 12 }}
      whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{
        duration: 0.45,
        delay: reduced ? 0 : index * 0.07,
        ease: [0.16, 1, 0.3, 1],
      }}
      whileHover={reduced ? undefined : { y: -3 }}
      className={cn(
        'group relative flex min-h-[140px] cursor-pointer items-center justify-center overflow-hidden rounded-2xl border bg-white px-4 py-5 shadow-sm transition-all hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fitlab-green focus-visible:ring-offset-2',
        isOpen
          ? 'border-fitlab-green/40 shadow-md ring-2 ring-fitlab-green/30'
          : 'border-fitlab-neutral-100 hover:border-fitlab-neutral-300',
      )}
    >
      {/* Línea superior tricolor — micro-acento de marca */}
      <span
        aria-hidden="true"
        className={cn(
          'absolute inset-x-0 top-0 mx-auto h-[3px] rounded-b-full bg-fitlab-gradient transition-all',
          isOpen ? 'w-24' : 'w-12 group-hover:w-20',
        )}
      />

      <img
        src={brand.logo}
        alt={brand.alt}
        loading="lazy"
        decoding="async"
        className="max-h-20 w-auto max-w-full object-contain"
      />
    </motion.button>
  )
}

type Item = {
  icon: LucideIcon
  text: string
  color: string
  ring: string
}

const BENEFITS: Item[] = [
  {
    icon: ClipboardCheck,
    text: 'Evaluación de salud de trabajadores',
    color: 'text-fitlab-green',
    ring: 'ring-fitlab-green/20',
  },
  {
    icon: AlertTriangle,
    text: 'Identificación de riesgos relevantes',
    color: 'text-orange-500',
    ring: 'ring-orange-500/20',
  },
  {
    icon: BarChart3,
    text: 'Informe general con enfoque estratégico',
    color: 'text-fitlab-blue',
    ring: 'ring-fitlab-blue/20',
  },
  {
    icon: Target,
    text: 'Apoyo real para mejorar la salud y productividad.',
    color: 'text-fitlab-red',
    ring: 'ring-fitlab-red/20',
  },
]

type DirectionCol = {
  icon: LucideIcon
  title: string
  description: string
  color: string
  ring: string
  accentBg: string
}

const DIRECTIONS: DirectionCol[] = [
  {
    icon: Stethoscope,
    title: 'Dirección médica',
    description: 'Enfoque clínico, experiencia en salud ocupacional y prevención de riesgos.',
    color: 'text-fitlab-green',
    ring: 'ring-fitlab-green/20',
    accentBg: 'bg-fitlab-green/5',
  },
  {
    icon: UserCog,
    title: 'Dirección administrativa',
    description: 'Gestión, operación y eficiencia para resultados concretos.',
    color: 'text-fitlab-blue',
    ring: 'ring-fitlab-blue/20',
    accentBg: 'bg-fitlab-blue/5',
  },
]

export default function Servicios() {
  const reduced = useReducedMotion()
  const [openId, setOpenId] = useState<SubBrandId | null>(null)
  const isMobile = useMediaQuery('(max-width: 767px)')
  const gridRef = useRef<HTMLDivElement | null>(null)

  const openBrand = openId ? (SUB_BRANDS.find((b) => b.id === openId) ?? null) : null
  const openIndex = openId ? SUB_BRANDS.findIndex((b) => b.id === openId) : -1
  const rowPosition: 'top' | 'bottom' = openIndex < 2 ? 'top' : 'bottom'

  // Click fuera del popover/grid cierra (solo desktop — mobile cierra por backdrop)
  useEffect(() => {
    if (!openId || isMobile) return
    const handler = (event: PointerEvent) => {
      const target = event.target as Node | null
      if (!target) return
      if (gridRef.current && gridRef.current.contains(target)) return
      const popoverEl = document
        .getElementById(`brand-info-title-${openId}`)
        ?.closest('[role="dialog"]')
      if (popoverEl && popoverEl.contains(target)) return
      setOpenId(null)
    }
    document.addEventListener('pointerdown', handler)
    return () => document.removeEventListener('pointerdown', handler)
  }, [openId, isMobile])

  const handleClose = () => setOpenId(null)
  const handleToggle = (id: SubBrandId) => setOpenId((curr) => (curr === id ? null : id))

  return (
    <section
      id="servicios"
      className="relative bg-fitlab-neutral-50 py-20 sm:py-24"
      aria-label="Servicios y beneficios FITLAB"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* HEADER — ancla principal de la sección */}
        <motion.div
          initial={reduced ? undefined : { opacity: 0, y: 14 }}
          whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
          className="mx-auto max-w-3xl text-center"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-fitlab-neutral-600">
            Nuestra propuesta
          </p>
          <h2 className="mt-3 text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">
            <span className="text-fitlab-blue">Medicina</span>{' '}
            <span className="text-fitlab-neutral-400">+</span>{' '}
            <span className="text-fitlab-red">Análisis</span>{' '}
            <span className="text-fitlab-neutral-400">+</span>{' '}
            <span className="text-fitlab-green">Gestión</span>
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-fitlab-neutral-600 sm:text-lg">
            Fitlab une criterio clínico con visión empresarial para entregar un servicio que impacta
            directamente en la operación de tu empresa.
          </p>
        </motion.div>

        {/* CONTENIDO — 2 columnas: checklist beneficios + grid sub-marcas */}
        <div className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-10">
          {/* Columna izquierda: ¿Qué recibe tu empresa? como card */}
          <motion.div
            initial={reduced ? undefined : { opacity: 0, x: -16 }}
            whileInView={reduced ? undefined : { opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-3xl border border-fitlab-neutral-100 bg-white p-6 shadow-sm sm:p-8"
          >
            <h3 className="text-xl font-bold tracking-tight text-fitlab-blue-hover sm:text-2xl">
              ¿Qué recibe tu empresa?
            </h3>
            <ul role="list" className="mt-6 divide-y divide-fitlab-neutral-100">
              {BENEFITS.map((item, i) => {
                const Icon = item.icon
                return (
                  <motion.li
                    key={item.text}
                    initial={reduced ? undefined : { opacity: 0, x: -8 }}
                    whileInView={reduced ? undefined : { opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{
                      duration: 0.4,
                      delay: reduced ? 0 : i * 0.07,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                    className="flex items-center gap-4 py-3.5"
                  >
                    <div
                      className={cn(
                        'flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white ring-2',
                        item.ring,
                      )}
                    >
                      <Icon
                        className={cn('h-5 w-5', item.color)}
                        strokeWidth={2}
                        aria-hidden="true"
                      />
                    </div>
                    <p className="text-sm font-medium text-fitlab-blue-hover sm:text-base">
                      {item.text}
                    </p>
                  </motion.li>
                )
              })}
            </ul>
          </motion.div>

          {/* Columna derecha: Familia de marcas */}
          <motion.div
            initial={reduced ? undefined : { opacity: 0, x: 16 }}
            whileInView={reduced ? undefined : { opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
          >
            <h3 className="text-xl font-bold tracking-tight text-fitlab-blue-hover sm:text-2xl">
              Familia FITLAB
            </h3>
            <p className="mt-2 text-sm text-fitlab-neutral-600">
              Ecosistema de marcas que se complementan para cubrir cada eje del servicio. Toca un
              logo para conocer más.
            </p>
            <div ref={gridRef} className="mt-6">
              {/* Popover desktop arriba del grid si rowPosition='bottom' */}
              {!isMobile && openBrand && rowPosition === 'bottom' && (
                <div className="mb-4">
                  <ServiceInfoPopover
                    brand={{
                      id: openBrand.id,
                      title: openBrand.title,
                      description: openBrand.description,
                      rowPosition,
                    }}
                    isMobile={false}
                    onClose={handleClose}
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                {SUB_BRANDS.map((b, i) => (
                  <SubBrandCard
                    key={b.id}
                    brand={b}
                    index={i}
                    isOpen={openId === b.id}
                    onClick={() => handleToggle(b.id)}
                  />
                ))}
              </div>
              {/* Popover desktop abajo del grid si rowPosition='top' */}
              {!isMobile && openBrand && rowPosition === 'top' && (
                <div className="mt-4">
                  <ServiceInfoPopover
                    brand={{
                      id: openBrand.id,
                      title: openBrand.title,
                      description: openBrand.description,
                      rowPosition,
                    }}
                    isMobile={false}
                    onClose={handleClose}
                  />
                </div>
              )}
            </div>
            {/* Bottom sheet mobile — render outside del grid pero dentro del componente */}
            {isMobile && (
              <ServiceInfoPopover
                brand={
                  openBrand
                    ? {
                        id: openBrand.id,
                        title: openBrand.title,
                        description: openBrand.description,
                        rowPosition,
                      }
                    : null
                }
                isMobile={true}
                onClose={handleClose}
              />
            )}
          </motion.div>
        </div>

        {/* DIRECCIONES — cards iguales abajo */}
        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6">
          {DIRECTIONS.map((d, i) => {
            const Icon = d.icon
            return (
              <motion.article
                key={d.title}
                initial={reduced ? undefined : { opacity: 0, y: 14 }}
                whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{
                  duration: 0.5,
                  delay: reduced ? 0 : i * 0.1,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className={cn(
                  'relative flex items-start gap-5 rounded-2xl border border-fitlab-neutral-100 p-6 shadow-sm',
                  d.accentBg,
                )}
              >
                <div
                  className={cn(
                    'flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white ring-2 shadow-sm',
                    d.ring,
                  )}
                >
                  <Icon className={cn('h-7 w-7', d.color)} strokeWidth={1.75} aria-hidden="true" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-fitlab-blue-hover sm:text-xl">{d.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-fitlab-neutral-600 sm:text-base">
                    {d.description}
                  </p>
                </div>
              </motion.article>
            )
          })}
        </div>
      </div>
    </section>
  )
}
